import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";

import { convertStringToSentenceCase } from "utilz/helpers";
import { Tooltip } from "components/tooltip";
import {
  IconOptionsMenu,
  IconOptionsMenuOption,
} from "../dropdown/IconOptionsMenu";

interface ActionButton {
  label: string;
  id: string;
  icon?: React.ReactNode;
  menuOptions?: Array<{ label: string; id: string; disabled?: boolean }>;
  endIcon?: React.ReactNode;
  /** Optional tooltip shown on hover (useful for explaining a disabled state). */
  tooltip?: string;
}

/**
 * Every action icon renders at 16×16, whatever size the caller passed
 * (most pass `fontSize="small"`, which is 20px) — design.
 */
const ActionIcon = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: "16px",
      height: "16px",
      flexShrink: 0,
      color: (theme) => theme.palette.neutral.main,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "& .MuiSvgIcon-root": { fontSize: "16px" },
    }}
  >
    {children}
  </Box>
);

type PositionMode = "screen-center" | "parent-relative";

interface ActionModalCustomProps {
  open?: boolean;
  onClose: () => void;
  selectedText?: string;
  buttons?: ActionButton[];
  onButtonClick: (id: string | number) => void;
  selectedCount?: number;
  disableButtonIds?: string[];
  /** When set, shows a spinner on this button and disables all buttons. */
  loadingButtonId?: string | null;
  /**
   * Position mode:
   * - 'screen-center': Fixed to bottom-center of viewport (default)
   * - 'parent-relative': Centered horizontally within parent, pinned to bottom
   */
  positionMode?: PositionMode;
  /** Per-instance style override applied to each button's label Typography. */
  labelSx?: SxProps<Theme>;
}

const ActionModalCustom = ({
  open,
  onClose = () => {},
  selectedText = "selected",
  buttons = [],
  onButtonClick,
  selectedCount = 0,
  disableButtonIds = [],
  loadingButtonId,
  positionMode = "screen-center",
  labelSx,
}: ActionModalCustomProps) => {
  const isParentRelative = positionMode === "parent-relative";

  // Tracks which dropdown option is currently mid-async-op. Set when the user
  // clicks an option, cleared once the parent's `loadingButtonId` clears —
  // this keeps the menu open + shows a loader on the clicked option.
  const [loadingOptionId, setLoadingOptionId] = useState<string | null>(null);
  useEffect(() => {
    if (!loadingButtonId) setLoadingOptionId(null);
  }, [loadingButtonId]);

  // For parent-relative mode, render without Dialog to avoid portal issues
  if (isParentRelative) {
    if (!(open || selectedCount > 0)) return null;

    return (
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1400, // Above MUI Drawer (1200) and Modal (1300)
        }}
      >
        <Stack
          direction="row"
          sx={{
            backgroundColor: "#10172E",
            borderRadius: "8px",
            height: "36px",
            border: "1px solid rgb(217, 217, 219)",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <Stack
            sx={{
              padding: "0px 16px",
              minWidth: "140px",
              backgroundColor: "#383940",
              height: "100%",
              justifyContent: "center",
              borderTopLeftRadius: "8px",
              borderBottomLeftRadius: "8px",
              cursor: "default",
            }}
          >
            <Typography variant="body-medium" color="white" noWrap>
              {selectedCount} {selectedText}
            </Typography>
          </Stack>
          <Stack direction="row" sx={{ height: "100%" }}>
            {buttons.map((button, index) => {
              const isLoading = loadingButtonId === button.id;
              const isDisabled =
                disableButtonIds.includes(button.id) || !!loadingButtonId;

              // If button has menuOptions, render with IconOptionsMenu
              if (button.menuOptions && button.menuOptions.length > 0) {
                const menuOptions: IconOptionsMenuOption[] =
                  button.menuOptions.map((option) => ({
                    label: option.label,
                    value: option.id,
                    icon: null,
                    disabled: isDisabled || !!option.disabled,
                  }));

                return (
                  <Stack
                    key={index}
                    direction="row"
                    sx={{
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: isDisabled ? undefined : "#232B64",
                      },
                      borderLeft: "1px solid rgb(217, 217, 219)",
                    }}
                  >
                    <IconOptionsMenu
                      // Opt-in: only buttons that pass `tooltip` show one (e.g. why disabled).
                      tooltipTitle={button.tooltip ?? ""}
                      keepOpenOnSelect
                      disabled={isDisabled}
                      loadingValue={
                        loadingButtonId === button.id ? loadingOptionId : null
                      }
                      buttonIconSx={{
                        width: "100%",
                        height: "100%",
                        padding: 0,
                        borderRadius: 0,
                        ":hover": { bgcolor: "transparent" },
                      }}
                      buttonIcon={
                        <Stack
                          key={index}
                          direction="row"
                          sx={{
                            padding: "0px 16px",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px",
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                          }}
                        >
                          {button.icon && (
                            <ActionIcon>{button.icon}</ActionIcon>
                          )}
                          <Typography
                            variant="body-small"
                            color="white"
                            noWrap
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {button.label}
                          </Typography>
                        </Stack>
                      }
                      options={menuOptions}
                      selected={[]}
                      onChange={(selectedValues) => {
                        if (selectedValues.length > 0 && !isDisabled) {
                          setLoadingOptionId(selectedValues[0]);
                          onButtonClick && onButtonClick(selectedValues[0]);
                        }
                      }}
                      selectionMode="single"
                      menuPaperSx={{
                        backgroundColor: "#FFFFFF",
                        border: "1px solid rgb(217, 217, 219)",
                        // Grows past 144px so a disabled option's reason isn't clipped.
                        width: "max-content",
                        minWidth: "144px",
                        maxWidth: "none",
                        marginTop: "-2px",
                        "& .MuiMenuItem-root": {
                          color: "#000000",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        },
                      }}
                    />
                  </Stack>
                );
              }

              // Regular button without menu options
              const buttonNode = (
                <Stack
                  key={index}
                  direction="row"
                  sx={{
                    padding: "0px 16px",
                    borderLeft: "1px solid rgb(217, 217, 219)",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "background-color 0.2s ease",
                    "&:hover": {
                      backgroundColor: isDisabled ? undefined : "#232B64",
                    },
                    gap: "8px",
                    opacity: isDisabled && !isLoading ? 0.5 : 1,
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  }}
                  onClick={() =>
                    isDisabled
                      ? null
                      : onButtonClick && onButtonClick(button.id || index)
                  }
                >
                  {isLoading ? (
                    <CircularProgress size={14} sx={{ color: "#FFF" }} />
                  ) : (
                    button.icon && <ActionIcon>{button.icon}</ActionIcon>
                  )}
                  <Typography
                    variant="body-small"
                    color="white"
                    noWrap
                    sx={labelSx}
                  >
                    {button.label
                      ? convertStringToSentenceCase(button.label)
                      : ""}
                  </Typography>
                  {button.endIcon}
                </Stack>
              );
              return button.tooltip ? (
                <Tooltip
                  key={index}
                  title={button.tooltip}
                  arrow
                  placement="top"
                >
                  <span style={{ display: "inline-flex" }}>{buttonNode}</span>
                </Tooltip>
              ) : (
                buttonNode
              );
            })}
          </Stack>
        </Stack>
      </Box>
    );
  }

  return (
    <Dialog
      maxWidth="lg"
      open={open || selectedCount > 0}
      onClose={onClose}
      // This is a floating selection toolbar, not a modal — it must not behave
      // like one. `disableEnforceFocus` is the load-bearing one: MUI's focus
      // trap listens for `focusin` document-wide and pulls focus back inside
      // whenever it lands elsewhere, so ANY inline editor opened while a
      // selection is active was focused, immediately blurred, and closed again
      // by its own blur handler (see InlineEditableText). It read as the field
      // blinking once and vanishing. The other three remove the same category
      // of modal behaviour: stealing focus on open, throwing it back on close,
      // and locking body scroll for as long as something is selected.
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      disableScrollLock
      style={{ position: "initial" }}
      BackdropProps={{ style: { backgroundColor: "transparent" } }}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          backgroundColor: "#0000",
          position: "absolute",
          width: "80%",
          left: "10%",
          right: "10%",
          bottom: 0,
          boxShadow: "none",
        },
      }}
    >
      <DialogContent
        sx={{
          width: "100%",
          borderRadius: "8px",
          backgroundColor: "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            // backgroundColor: "red",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack
            direction="row"
            sx={{
              backgroundColor: "#10172E",
              borderRadius: "8px",
              height: "36px",
              border: "1px solid rgb(217, 217, 219)",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            <Stack
              sx={{
                padding: "0px 16px",
                minWidth: "140px",
                backgroundColor: "#383940",
                height: "100%",
                justifyContent: "center",
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
                cursor: "default",
              }}
            >
              <Typography variant="body-medium" color="white">
                {selectedCount} {selectedText}
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ height: "100%" }}>
              {buttons.map((button, index) => {
                const isDisabled = disableButtonIds.includes(button.id);

                // If button has menuOptions, render with IconOptionsMenu
                if (button.menuOptions && button.menuOptions.length > 0) {
                  const menuOptions: IconOptionsMenuOption[] =
                    button.menuOptions.map((option) => ({
                      label: option.label,
                      value: option.id,
                      icon: null,
                      disabled: isDisabled || !!option.disabled,
                    }));

                  return (
                    <Stack
                      key={index}
                      direction="row"
                      sx={{
                        transition: "background-color 0.2s ease",
                        "&:hover": {
                          backgroundColor: isDisabled ? undefined : "#232B64",
                        },
                        borderLeft: "1px solid rgb(217, 217, 219)",
                      }}
                    >
                      {/* {button.icon} */}

                      <IconOptionsMenu
                        // Opt-in: only buttons that pass `tooltip` show one (e.g. why disabled).
                        tooltipTitle={button.tooltip ?? ""}
                        keepOpenOnSelect
                        disabled={isDisabled}
                        loadingValue={
                          loadingButtonId === button.id ? loadingOptionId : null
                        }
                        buttonIconSx={{
                          width: "100%",
                          height: "100%",
                          padding: 0,
                          borderRadius: 0,
                          ":hover": { bgcolor: "transparent" },
                        }}
                        buttonIcon={
                          <Stack
                            key={index}
                            direction="row"
                            sx={{
                              // minWidth: "140px",
                              padding: "0px 16px",
                              // borderLeft: "1px solid rgb(217, 217, 219)",
                              justifyContent: "center",
                              alignItems: "center",
                              // transition: "background-color 0.2s ease",
                              // "&:hover": {
                              //   backgroundColor: "#232B64",
                              // },
                              gap: "8px",
                              opacity: isDisabled ? 0.5 : 1,
                              cursor: isDisabled ? "not-allowed" : "pointer",
                            }}
                          >
                            {button.icon && (
                              <ActionIcon>{button.icon}</ActionIcon>
                            )}

                            <Typography
                              variant="body-small"
                              color="white"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {button.label}
                            </Typography>
                          </Stack>
                        }
                        options={menuOptions}
                        selected={[]}
                        onChange={(selectedValues) => {
                          if (selectedValues.length > 0 && !isDisabled) {
                            setLoadingOptionId(selectedValues[0]);
                            onButtonClick && onButtonClick(selectedValues[0]);
                          }
                        }}
                        selectionMode="single"
                        menuPaperSx={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid rgb(217, 217, 219)",
                          // Grows past 144px so a disabled option's reason isn't clipped.
                          width: "max-content",
                          minWidth: "144px",
                          maxWidth: "none",
                          marginTop: "-2px",
                          "& .MuiMenuItem-root": {
                            color: "#000000",
                            whiteSpace: "nowrap",
                            "&:hover": {
                              backgroundColor: "#f5f5f5",
                            },
                          },
                        }}
                      />
                    </Stack>
                  );
                }

                // Regular button without menu options
                const buttonNode = (
                  <Stack
                    key={index}
                    direction="row"
                    sx={{
                      // minWidth: "140px",
                      padding: "0px 16px",
                      borderLeft: "1px solid rgb(217, 217, 219)",
                      justifyContent: "center",
                      alignItems: "center",
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#232B64",
                      },
                      gap: "8px",
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                    onClick={() =>
                      isDisabled
                        ? null
                        : onButtonClick && onButtonClick(button.id || index)
                    }
                  >
                    {button.icon && <ActionIcon>{button.icon}</ActionIcon>}
                    <Typography variant="body-small" color="white" sx={labelSx}>
                      {button.label
                        ? convertStringToSentenceCase(button.label)
                        : ""}
                    </Typography>
                    {button.endIcon}
                  </Stack>
                );
                return button.tooltip ? (
                  <Tooltip
                    key={index}
                    title={button.tooltip}
                    arrow
                    placement="top"
                  >
                    <span style={{ display: "inline-flex" }}>{buttonNode}</span>
                  </Tooltip>
                ) : (
                  buttonNode
                );
              })}
            </Stack>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ActionModalCustom;
